import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FlaskConical, Loader2, Wand2, Download, Trash2, Pencil,
  Plus, ChevronDown, ChevronUp, CheckCircle2, FileCode2
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CCBSkillHint from "@/components/CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  review: "bg-accent/15 text-accent",
  approved: "bg-primary/15 text-primary",
};

interface Scenario {
  name: string;
  tags: string[];
  given: string[];
  when: string[];
  then: string[];
  and?: string[];
}

interface TestCase {
  id: string;
  title: string;
  feature_tag: string;
  scenarios: Scenario[];
  status: string;
  framework: string;
  requirement_id?: string;
  created_at: string;
}

interface Props {
  baProjectId: string;
}

export default function TestCasesPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [requirements, setRequirements] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterReq, setFilterReq] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Generate wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<"pick" | "generating" | "edit" | "confirm">("pick");
  const [wizardReqId, setWizardReqId] = useState<string>("");
  const [wizardExtra, setWizardExtra] = useState<string>("");
  const [wizardDraft, setWizardDraft] = useState<any>(null);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [ragUsed, setRagUsed] = useState(false);

  // Edit dialog state
  const [editingTc, setEditingTc] = useState<TestCase | null>(null);

  useEffect(() => {
    if (baProjectId) {
      loadTestCases();
      loadRequirements();
    }
  }, [baProjectId]);

  const loadRequirements = async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/tools/projects/${baProjectId}`, { headers: authHeader });
      setRequirements((data.requirements || []).map((r: any) => ({ id: r.id, title: r.title })));
    } catch {
      setRequirements([]);
    }
  };

  const loadTestCases = async () => {
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/test-cases?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setTestCases(data);
    } catch {
      setTestCases([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Wizard ──────────────────────────────────────────────────────────────────
  const openWizard = () => {
    setWizardStep("pick");
    setWizardReqId(requirements[0]?.id || "");
    setWizardExtra("");
    setWizardDraft(null);
    setWizardError(null);
    setWizardOpen(true);
  };

  const runGenerate = async () => {
    if (!wizardReqId) return;
    setWizardStep("generating");
    setWizardError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/generate-test-cases`,
        {
          requirement_id: wizardReqId,
          project_id: baProjectId,
          business_case_context: wizardExtra.trim() || null,
        },
        { headers: authHeader }
      );
      setWizardDraft(data.draft);
      setRagUsed(data.rag_context_used);
      setWizardStep("edit");
    } catch (e: any) {
      setWizardError(e?.response?.data?.detail || e?.message || "Generation failed");
      setWizardStep("pick");
    }
  };

  const confirmWizard = async () => {
    if (!wizardDraft) return;
    setWizardStep("confirm");
    try {
      await axios.post(
        `${BASE}/api/tools/test-cases`,
        {
          project_id: baProjectId,
          requirement_id: wizardReqId || null,
          title: wizardDraft.feature_title,
          feature_tag: wizardDraft.feature_tag,
          scenarios: wizardDraft.scenarios,
          status: "draft",
          framework: "gherkin",
        },
        { headers: authHeader }
      );
      setWizardOpen(false);
      loadTestCases();
    } catch (e: any) {
      setWizardError(e?.response?.data?.detail || e?.message || "Save failed");
      setWizardStep("edit");
    }
  };

  // ── Scenario editor helpers ──────────────────────────────────────────────────
  const updateScenario = (draft: any, idx: number, field: string, value: any) => {
    const s = [...draft.scenarios];
    s[idx] = { ...s[idx], [field]: value };
    return { ...draft, scenarios: s };
  };

  const updateStep = (draft: any, scenIdx: number, field: "given" | "when" | "then", stepIdx: number, value: string) => {
    const scenarios = [...draft.scenarios];
    const steps = [...(scenarios[scenIdx][field] || [])];
    steps[stepIdx] = value;
    scenarios[scenIdx] = { ...scenarios[scenIdx], [field]: steps };
    return { ...draft, scenarios };
  };

  const addStep = (draft: any, scenIdx: number, field: "given" | "when" | "then") => {
    const scenarios = [...draft.scenarios];
    scenarios[scenIdx] = { ...scenarios[scenIdx], [field]: [...(scenarios[scenIdx][field] || []), ""] };
    return { ...draft, scenarios };
  };

  const removeStep = (draft: any, scenIdx: number, field: "given" | "when" | "then", stepIdx: number) => {
    const scenarios = [...draft.scenarios];
    scenarios[scenIdx] = { ...scenarios[scenIdx], [field]: (scenarios[scenIdx][field] || []).filter((_: any, i: number) => i !== stepIdx) };
    return { ...draft, scenarios };
  };

  const addScenario = (draft: any) => ({
    ...draft,
    scenarios: [...draft.scenarios, { name: "New scenario", tags: [], given: [""], when: [""], then: [""] }],
  });

  const removeScenario = (draft: any, idx: number) => ({
    ...draft,
    scenarios: draft.scenarios.filter((_: any, i: number) => i !== idx),
  });

  // ── Export ───────────────────────────────────────────────────────────────────
  const toGherkin = (tc: TestCase): string => {
    const lines: string[] = [];
    lines.push(`Feature: ${tc.title}`);
    lines.push(`  # ${tc.feature_tag || ""}`);
    lines.push("");
    for (const s of tc.scenarios) {
      if (s.tags?.length) lines.push(`  ${s.tags.join(" ")}`);
      lines.push(`  Scenario: ${s.name}`);
      (s.given || []).forEach((g, i) => lines.push(`    ${i === 0 ? "Given" : "And"} ${g}`));
      (s.when || []).forEach((w, i) => lines.push(`    ${i === 0 ? "When" : "And"} ${w}`));
      (s.then || []).forEach((t, i) => lines.push(`    ${i === 0 ? "Then" : "And"} ${t}`));
      lines.push("");
    }
    return lines.join("\n");
  };

  const toPlaywright = (tc: TestCase): string => {
    const lines: string[] = [];
    lines.push(`import { test, expect } from '@playwright/test';`);
    lines.push("");
    lines.push(`// Feature: ${tc.title}`);
    for (const s of tc.scenarios) {
      const testName = s.name;
      lines.push(`test('${testName.replace(/'/g, "\\'")}', async ({ page }) => {`);
      if (s.given?.length) {
        lines.push(`  // Given`);
        s.given.forEach(g => lines.push(`  // ${g}`));
      }
      if (s.when?.length) {
        lines.push(`  // When`);
        s.when.forEach(w => lines.push(`  // ${w}`));
        lines.push(`  // TODO: implement actions above`);
      }
      if (s.then?.length) {
        lines.push(`  // Then`);
        s.then.forEach(t => lines.push(`  // ${t}`));
        lines.push(`  // TODO: add expect() assertions above`);
      }
      lines.push(`});`);
      lines.push("");
    }
    return lines.join("\n");
  };

  const exportFile = (tc: TestCase, format: "gherkin" | "playwright") => {
    const content = format === "gherkin" ? toGherkin(tc) : toPlaywright(tc);
    const ext = format === "gherkin" ? ".feature" : ".spec.ts";
    const slug = tc.title.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}${ext}`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleDelete = async (id: string) => {
    await axios.delete(`${BASE}/api/tools/test-cases/${id}`, { headers: authHeader });
    setTestCases((prev) => prev.filter((tc) => tc.id !== id));
  };

  const handleStatusChange = async (id: string, status: string) => {
    await axios.patch(`${BASE}/api/tools/test-cases/${id}`, { status }, { headers: authHeader });
    setTestCases((prev) => prev.map((tc) => tc.id === id ? { ...tc, status } : tc));
  };

  // ── Filtered list ────────────────────────────────────────────────────────────
  const filtered = testCases.filter((tc) => {
    if (filterReq !== "all" && tc.requirement_id !== filterReq) return false;
    if (filterStatus !== "all" && tc.status !== filterStatus) return false;
    return true;
  });

  // ── Shared scenario editor (used in both wizard and edit dialog) ─────────────
  const ScenarioEditor = ({ draft, setDraft }: { draft: any; setDraft: (d: any) => void }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Feature title</p>
          <Input value={draft.feature_title || ""} onChange={e => setDraft({ ...draft, feature_title: e.target.value })} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Feature tag</p>
          <Input value={draft.feature_tag || ""} onChange={e => setDraft({ ...draft, feature_tag: e.target.value })} placeholder="@tag_name" />
        </div>
      </div>
      {(draft.scenarios || []).map((s: Scenario, si: number) => (
        <div key={si} className="border border-border rounded-md p-3 space-y-2 bg-muted/20">
          <div className="flex items-center justify-between gap-2">
            <Input
              className="h-7 text-xs font-semibold"
              value={s.name}
              onChange={e => setDraft(updateScenario(draft, si, "name", e.target.value))}
              placeholder="Scenario name"
            />
            <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => setDraft(removeScenario(draft, si))}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex gap-1 flex-wrap">
            {(s.tags || []).map((t, ti) => (
              <Badge key={ti} variant="outline" className="text-[10px] gap-1 cursor-pointer" onClick={() => {
                const tags = s.tags.filter((_: string, i: number) => i !== ti);
                setDraft(updateScenario(draft, si, "tags", tags));
              }}>{t} ×</Badge>
            ))}
            <Input
              className="h-5 w-24 text-[10px] px-1"
              placeholder="+ tag"
              onKeyDown={e => {
                if (e.key === "Enter") {
                  const val = (e.target as HTMLInputElement).value.trim();
                  if (val) {
                    setDraft(updateScenario(draft, si, "tags", [...(s.tags || []), val.startsWith("@") ? val : `@${val}`]));
                    (e.target as HTMLInputElement).value = "";
                  }
                }
              }}
            />
          </div>
          {(["given", "when", "then"] as const).map(field => (
            <div key={field}>
              <p className="text-[10px] font-mono font-semibold text-muted-foreground uppercase mb-1">{field}</p>
              {(s[field] || []).map((step: string, stepIdx: number) => (
                <div key={stepIdx} className="flex gap-1 mb-1">
                  <Input
                    className="h-6 text-xs"
                    value={step}
                    onChange={e => setDraft(updateStep(draft, si, field, stepIdx, e.target.value))}
                    placeholder={`${field} step…`}
                  />
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0 text-muted-foreground" onClick={() => setDraft(removeStep(draft, si, field, stepIdx))}>×</Button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="h-6 text-[11px] text-muted-foreground gap-1 px-1" onClick={() => setDraft(addStep(draft, si, field))}>
                <Plus className="h-3 w-3" /> Add step
              </Button>
            </div>
          ))}
        </div>
      ))}
      <Button size="sm" variant="outline" className="w-full gap-1.5 text-xs" onClick={() => setDraft(addScenario(draft))}>
        <Plus className="h-3.5 w-3.5" /> Add Scenario
      </Button>
    </div>
  );

  return (
    <div className="p-4 space-y-4">
    <CCBSkillHint tab="test-cases" className="mb-4" />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Test Cases</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{testCases.length} total · Gherkin / Playwright</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={openWizard} disabled={requirements.length === 0}>
          <Wand2 className="h-3.5 w-3.5" /> Generate from Requirement
        </Button>
      </div>

      {requirements.length === 0 && (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          Create requirements first — test cases are generated from requirements + your uploaded business case documents.
        </div>
      )}

      {/* Filters */}
      {testCases.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterReq}
            onChange={(e) => setFilterReq(e.target.value)}
            className="h-7 text-xs border border-input rounded-md px-2 bg-background"
          >
            <option value="all">All requirements</option>
            {requirements.map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-7 text-xs border border-input rounded-md px-2 bg-background"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="approved">Approved</option>
          </select>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FlaskConical className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm font-semibold text-foreground mb-1">{testCases.length === 0 ? "No test cases yet" : "No test cases match filters"}</p>
          <p className="text-xs text-muted-foreground">Upload business case documents in Knowledge Base, then generate from a requirement.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((tc) => {
            const reqTitle = requirements.find(r => r.id === tc.requirement_id)?.title;
            const isExpanded = expandedId === tc.id;
            return (
              <div key={tc.id} className="border border-border rounded-md bg-card">
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-card-foreground">{tc.title}</p>
                      {reqTitle && <p className="text-[11px] text-muted-foreground font-mono mt-0.5">← {reqTitle}</p>}
                    </div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                      <Badge variant="outline" className={`text-[10px] px-1.5 ${statusColors[tc.status]}`}>{tc.status}</Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 font-mono">{tc.feature_tag}</Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5">{tc.scenarios.length} scenario{tc.scenarios.length !== 1 ? "s" : ""}</Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Select value={tc.status} onValueChange={(v) => handleStatusChange(tc.id, v)}>
                      <SelectTrigger className="h-7 w-28 text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="review">Review</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-[11px]" onClick={() => setEditingTc(tc)}>
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-[11px]" onClick={() => exportFile(tc, "gherkin")}>
                      <Download className="h-3 w-3" /> .feature
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-[11px]" onClick={() => exportFile(tc, "playwright")}>
                      <FileCode2 className="h-3 w-3" /> Playwright
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-[11px] text-muted-foreground" onClick={() => setExpandedId(isExpanded ? null : tc.id)}>
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {isExpanded ? "Hide" : "Preview"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => handleDelete(tc.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                    {tc.scenarios.map((s, i) => (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-mono font-semibold text-foreground">Scenario: {s.name}</span>
                          {(s.tags || []).map((t) => (
                            <Badge key={t} variant="outline" className="text-[9px] px-1">{t}</Badge>
                          ))}
                        </div>
                        <div className="font-mono text-[11px] space-y-0.5 text-muted-foreground">
                          {(s.given || []).map((g, gi) => <p key={gi} className="pl-2"><span className="text-accent">{gi === 0 ? "Given" : "And"}</span> {g}</p>)}
                          {(s.when || []).map((w, wi) => <p key={wi} className="pl-2"><span className="text-primary">{wi === 0 ? "When" : "And"}</span> {w}</p>)}
                          {(s.then || []).map((t, ti) => <p key={ti} className="pl-2"><span className="text-green-600">{ti === 0 ? "Then" : "And"}</span> {t}</p>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Generate Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={(v) => { if (!v) setWizardOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FlaskConical className="h-4 w-4" />
              {wizardStep === "pick" && "Generate Test Cases"}
              {wizardStep === "generating" && "Generating with Llama…"}
              {wizardStep === "edit" && "Review & Edit Scenarios"}
              {wizardStep === "confirm" && "Saving…"}
            </DialogTitle>
          </DialogHeader>

          {wizardStep === "pick" && (
            <div className="space-y-4 pt-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Select requirement</p>
                <select
                  value={wizardReqId}
                  onChange={(e) => setWizardReqId(e.target.value)}
                  className="w-full h-9 text-sm border border-input rounded-md px-3 bg-background"
                >
                  {requirements.map((r) => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Additional business case context <span className="text-muted-foreground/60">(optional — paste a policy excerpt, regulation text, or acceptance criteria)</span>
                </p>
                <Textarea
                  placeholder="e.g. According to AU Privacy Act 1988 s.16A, entities must not use health information for a purpose other than the primary purpose..."
                  value={wizardExtra}
                  onChange={(e) => setWizardExtra(e.target.value)}
                  rows={5}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Your uploaded Knowledge Base documents will also be searched automatically.
                </p>
              </div>
              {wizardError && <p className="text-xs text-destructive">{wizardError}</p>}
              <Button className="w-full gap-1.5" onClick={runGenerate} disabled={!wizardReqId}>
                <Wand2 className="h-3.5 w-3.5" /> Generate Gherkin Scenarios
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                AI drafts only — you review and edit everything before saving
              </p>
            </div>
          )}

          {wizardStep === "generating" && (
            <div className="py-16 flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Llama is generating test scenarios…</p>
              <p className="text-xs">Searching knowledge base for relevant business case context</p>
            </div>
          )}

          {wizardStep === "edit" && wizardDraft && (
            <div className="space-y-4 pt-2">
              {ragUsed && (
                <p className="text-xs text-accent flex items-center gap-1">
                  ✦ Knowledge base was searched — scenarios include policy context
                </p>
              )}
              <ScenarioEditor draft={wizardDraft} setDraft={setWizardDraft} />
              {wizardError && <p className="text-xs text-destructive">{wizardError}</p>}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setWizardStep("pick")}>Back</Button>
                <Button className="flex-1 gap-1.5" onClick={confirmWizard}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Confirm & Save
                </Button>
              </div>
            </div>
          )}

          {wizardStep === "confirm" && (
            <div className="py-16 flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Saving test case…</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {editingTc && (
        <Dialog open={!!editingTc} onOpenChange={(v) => { if (!v) setEditingTc(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">Edit Test Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <ScenarioEditor
                draft={{ feature_title: editingTc.title, feature_tag: editingTc.feature_tag, scenarios: editingTc.scenarios }}
                setDraft={(d: any) => setEditingTc({ ...editingTc, title: d.feature_title, feature_tag: d.feature_tag, scenarios: d.scenarios })}
              />
              <Button className="w-full gap-1.5" onClick={async () => {
                await axios.patch(
                  `${BASE}/api/tools/test-cases/${editingTc.id}`,
                  { title: editingTc.title, feature_tag: editingTc.feature_tag, scenarios: editingTc.scenarios },
                  { headers: authHeader }
                );
                setEditingTc(null);
                loadTestCases();
              }}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
